import { memo, useMemo } from "react";
import type { EChartsOption, SeriesOption } from "echarts";
import EChart from "./EChart";
import { defaultAxisLabel, defaultGrid, tooltipCss } from "./chartDefaults";
import type { Timeseries } from "../../types";
import type { AxisFormatter } from "./XmrChart";

interface PChartProps {
  successes: Timeseries;
  trials: Timeseries;
  axisFormatter?: AxisFormatter;
  axisName?: string;
}

interface PChartPoint {
  timestamp: number;
  proportion: number;
  upperLimit: number;
  lowerLimit: number;
  sampleSize: number;
}

function PChart({ successes, trials, axisFormatter, axisName }: PChartProps) {
  const data = useMemo(() => buildPChart(successes, trials), [successes, trials]);
  const option = useMemo<EChartsOption>(
    () => buildOption(data, axisFormatter, axisName),
    [data, axisFormatter, axisName],
  );
  return <EChart option={option} height={400} />;
}

function buildPChart(successes: Timeseries, trials: Timeseries): { points: PChartPoint[]; center: number } {
  const trialMap = new Map<number, number>();
  for (let i = 0; i < trials.timestamps.length; i++) {
    trialMap.set(trials.timestamps[i].getTime(), trials.data[i]);
  }

  const samples: { timestamp: number; successes: number; trials: number }[] = [];
  let totalSuccesses = 0;
  let totalTrials = 0;

  for (let i = 0; i < successes.timestamps.length; i++) {
    const timestamp = successes.timestamps[i].getTime();
    const n = Number(trialMap.get(timestamp) ?? 0);
    if (n <= 0) continue;
    const s = successes.data[i];
    samples.push({ timestamp, successes: s, trials: n });
    totalSuccesses += s;
    totalTrials += n;
  }

  if (samples.length === 0 || totalTrials === 0) {
    return { points: [], center: 0 };
  }

  const center = totalSuccesses / totalTrials;

  const points: PChartPoint[] = samples.map(sample => {
    const proportion = sample.successes / sample.trials;
    const sigma = Math.sqrt((center * (1 - center)) / sample.trials);
    const upper = Math.min(1, center + 3 * sigma);
    const lower = Math.max(0, center - 3 * sigma);
    return {
      timestamp: sample.timestamp,
      proportion,
      upperLimit: upper,
      lowerLimit: lower,
      sampleSize: sample.trials,
    } satisfies PChartPoint;
  });

  return { points, center };
}

function buildOption(
  data: { points: PChartPoint[]; center: number },
  axisFormatter?: AxisFormatter,
  axisName?: string,
): EChartsOption {
  const timestamps = data.points.map(point => point.timestamp);
  const values = data.points.flatMap(point => [point.proportion, point.upperLimit, point.lowerLimit]);
  values.push(data.center);
  const finiteValues = values.filter(value => Number.isFinite(value));
  const seriesMin = finiteValues.length ? Math.min(...finiteValues) : 0;
  const seriesMax = finiteValues.length ? Math.max(...finiteValues) : 1;
  const span = Math.max(seriesMax - seriesMin, 0.01);
  const padding = Math.max(span * 0.1, 0.01);
  const axisMin = Math.max(0, seriesMin - padding);
  const axisMax = Math.min(1, seriesMax + padding);
  const valueSeries: SeriesOption = {
    name: "CTR",
    type: "line",
    data: data.points.map(point => [point.timestamp, point.proportion]),
    symbol: "circle",
    symbolSize: 6,
    lineStyle: { color: "#111" },
    itemStyle: { color: "#111" },
    emphasis: { focus: "series" },
    smooth: true,
  };

  const centerSeries: SeriesOption = {
    name: "Center",
    type: "line",
    data: timestamps.map(ts => [ts, data.center]),
    showSymbol: false,
    lineStyle: { color: "#8C8B7C", type: "dashed" },
    emphasis: { disabled: true },
  };

  const uclSeries: SeriesOption = {
    name: "UCL",
    type: "line",
    data: data.points.map(point => [point.timestamp, point.upperLimit]),
    showSymbol: false,
    lineStyle: { color: "#BFBDA7", type: "dashed" },
    emphasis: { disabled: true },
  };

  const lclSeries: SeriesOption = {
    name: "LCL",
    type: "line",
    data: data.points.map(point => [point.timestamp, point.lowerLimit]),
    showSymbol: false,
    lineStyle: { color: "#BFBDA7", type: "dashed" },
    emphasis: { disabled: true },
  };

  const anomalies = data.points.filter(
    point => point.proportion > point.upperLimit || point.proportion < point.lowerLimit,
  );
  const anomalySeries: SeriesOption | undefined = anomalies.length
    ? {
        name: "Outside limits",
        type: "scatter",
        data: anomalies.map(point => [point.timestamp, point.proportion]),
        symbol: "circle",
        symbolSize: 10,
        itemStyle: {
          color: "#ffffff",
          borderColor: "#ff0000",
          borderWidth: 3,
        },
        emphasis: { focus: "series" },
      }
    : undefined;

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      extraCssText: tooltipCss,
      valueFormatter: axisFormatter ? value => axisFormatter(Number(value)) : undefined,
    },
    xAxis: {
      type: "time",
      boundaryGap: false,
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: axisFormatter ?? defaultAxisLabel, margin: 12 },
      min: axisMin,
      max: axisMax,
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
    grid: defaultGrid,
    series: anomalySeries
      ? [valueSeries, centerSeries, uclSeries, lclSeries, anomalySeries]
      : [valueSeries, centerSeries, uclSeries, lclSeries],
  } satisfies EChartsOption;
}

export default memo(PChart);
